import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initializeTawkTo, removeTawkTo } from "utils/tawkto";
import { exactPaths } from "./AdminLayout";

export const TawkToHandler = () => {
  const location = useLocation();
  const observerRef = useRef<MutationObserver | null>(null);
  const isRestrictedRef = useRef<boolean>(false);

  // Global listeners setup
  useEffect(() => {
    window.Tawk_API = window.Tawk_API || {};

    // Set up onLoad handler once, ensuring it relies on the current ref value
    if (!window.Tawk_API._isCustomOnLoadSet) {
      const originalOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = () => {
        if (originalOnLoad) originalOnLoad();
        if (isRestrictedRef.current) {
          removeTawkTo();
        } else {
          window.Tawk_API?.showWidget?.();
        }
      };
      window.Tawk_API._isCustomOnLoadSet = true;
    }

    // Fail-safe for returning to a throttled or background tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (isRestrictedRef.current) {
          removeTawkTo();
        } else if (!isRestrictedRef.current && window.Tawk_API?.showWidget) {
          window.Tawk_API.showWidget();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const isAgentPage = location.pathname.includes("agent");
    const hasUser = !!(
      localStorage.getItem("vle-user") || sessionStorage.getItem("vle-user")
    );
    const isAuthRoute =
      location.pathname === "/login" ||
      location.pathname === "/forgot-password";

    const isRestricted =
      exactPaths.includes(location.pathname) ||
      isAgentPage ||
      (hasUser && isAuthRoute);

    isRestrictedRef.current = isRestricted;

    const lockTitle = () => {
      if (document.title !== "VLE Mobile") {
        document.title = "VLE Mobile";
      }
    };

    if (isRestricted) {
      removeTawkTo();
      lockTitle();

      if (!observerRef.current) {
        const titleElement = document.querySelector("title");
        if (titleElement) {
          observerRef.current = new MutationObserver(() => {
            lockTitle();
          });
          observerRef.current.observe(titleElement, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      }
    } else {
      initializeTawkTo();

      // Apply custom settings to Tawk_API after ensure it exists or was reset
      window.Tawk_API = window.Tawk_API || {};
      if (!window.Tawk_API._isCustomOnLoadSet) {
        window.Tawk_API.onLoad = () => {
          if (isRestrictedRef.current) {
            removeTawkTo();
          } else {
            window.Tawk_API?.showWidget?.();
          }
        };
        window.Tawk_API._isCustomOnLoadSet = true;
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      window.Tawk_API?.showWidget?.();
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [location.pathname]);

  return null;
};
